import { css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';
import type { FC } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { SettingsModal } from '@/features/settings/components/SettingsModal';
import { MainSidebarUserProfile } from '@/shared/components/layout/MainSidebarUserProfile';
import { Logo, type LogoVariant } from '@/shared/components/ui/Logo';
import { MenuCloseButton } from '@/shared/components/ui/MenuCloseButton';
import { NavItem } from '@/shared/components/ui/NavItem';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

interface NavigationItem {
  icon: LogoVariant;
  label: string;
  path: string;
}

interface MainSidebarProps {
  onClose?: () => void;
  onLogoClick?: () => void;
}

export const MainSidebar: FC<MainSidebarProps> = ({ onClose, onLogoClick }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    {
      icon: 'cube',
      label: 'Produits',
      path: '/analysis/products',
    },
    {
      icon: 'basket',
      label: 'Vendeurs',
      path: '/analysis/vendors',
    },
    {
      icon: 'globe',
      label: 'Publiques',
      path: '/analysis/public',
    },
  ];

  const sidebarStyles = css({
    w: isMobile ? '14.0625rem' : '10rem',
    maxW: isMobile ? '14.0625rem' : 'none',
    h: '100vh',
    bg: '#101115',
    display: 'flex',
    flexDirection: 'column',
    position: isMobile ? 'fixed' : 'relative',
    right: isMobile ? 0 : 'auto',
    top: isMobile ? 0 : 'auto',
    zIndex: isMobile ? 50 : 'auto',
    flexShrink: 0,
    animation: isMobile ? 'slideInRight 0.3s ease-out' : 'none',
  });

  const headerStyles = stack({
    direction: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    mt: '0.69rem',
    px: '0.62rem',
    gap: 'xs',
  });

  const appNameStyles = css({
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: '1.2044rem', // 19.271px
    fontWeight: 'bold',
    color: 'white.default',
    textAlign: 'center',
    lineHeight: 1.07,
  });

  const appLogoStyles = css({
    width: '1.25rem',
    height: '2rem',
  });

  const appNameSecondaryStyles = css({
    color: '#56575e',
  });

  const navStyles = css({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
    overflowX: 'clip',
    gap: 'lg',
    px: '0.63rem',
    py: '2.625rem',
    mt: isMobile ? '1.875rem' : '0', // 30px margin-top on mobile
  });

  const sectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'xs',
    w: '100%',
  });

  const sectionTitleStyles = css({
    fontSize: '0.625rem',
    fontWeight: 600,
    color: '#4f5055',
    textTransform: 'uppercase',
    lineHeight: 1.07,
  });

  const navListStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'xs',
    w: '100%',
  });

  const footerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    px: '0.5625rem',
    py: '0.5625rem',
    gap: '1.8125rem',
  });

  const versionStyles = css({
    fontSize: '0.5rem', // 8px
    fontWeight: 600,
    color: '#4f5055',
    lineHeight: 1.07,
  });

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleDiscordClick = () => {
    const discordUrl = import.meta.env.VITE_DISCORD_INVITE_URL;
    window.open(discordUrl, '_blank', 'noopener,noreferrer');
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  const closeButtonPositionStyles = css({
    position: 'absolute',
    top: '35px',
    right: '18px',
  });

  return (
    <div className={sidebarStyles}>
      {/* Close button for mobile */}
      {isMobile && onClose && <MenuCloseButton onClick={onClose} className={closeButtonPositionStyles} />}

      {/* App name header */}
      {!isMobile && (
        <button className={headerStyles} onClick={onLogoClick} type="button">
          <Logo variant="cube" className={appLogoStyles} />
          <div className={appNameStyles}>
            RE<span className={appNameSecondaryStyles}>COP</span>
          </div>
        </button>
      )}

      {/* Navigation */}
      <div className={navStyles}>
        {/* Section Analyses */}
        <div className={sectionStyles}>
          <div className={sectionTitleStyles}>ANALYSES</div>
          <div className={navListStyles}>
            {navigationItems.map((item) => (
              <NavItem
                key={item.path}
                icon={item.icon}
                label={item.label}
                isActive={location.pathname.startsWith(item.path)}
                onClick={() => handleNavigation(item.path)}
              />
            ))}
          </div>
        </div>

        {/* Section Communauté */}
        <div className={sectionStyles}>
          <div className={sectionTitleStyles}>COMMUNAUTÉ</div>
          <div className={navListStyles}>
            <NavItem icon="discord" label="Discord" isActive={false} onClick={handleDiscordClick} />
            <NavItem icon="message" label="Contact" isActive={false} onClick={handleContactClick} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={footerStyles}>
        <MainSidebarUserProfile
          username={user?.email || 'Utilisateur'}
          onSubscriptionClick={() => navigate('/subscription')}
          onSettingsClick={() => setIsSettingsModalOpen(true)}
          onLogoutClick={() => logout()}
        />
        <div className={versionStyles}>2025 recop.app v1.00</div>
      </div>

      {/* Settings Modal */}
      <SettingsModal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
    </div>
  );
};
